import {type ModeTokens} from "../theme/tokens";

/**
 * File-kind badges.
 *
 * **Decision (kitecorp/transferify-ui#334): the badge captions are FORMAT TOKENS and stay
 * untranslated.** They are the file's format written the way the format itself is written —
 * `senderFileKind` literally uppercases the extension, so an `.xlsx` badge reads "XLSX" in every
 * language and there is no coherent way to translate half the vocabulary. The recipient badges are
 * `aria-hidden` (see `recipient/RecipientFiles.tsx`), so nothing here is ever read aloud either.
 * They therefore do NOT belong in the message catalog, for the same reason SI unit symbols
 * ("GB", "TB") do not — see `core/format/byteFormat.ts`.
 */

/** File-kind identities shared by every file badge, independent of theme presentation. */
export type FileKindTone = "image" | "video" | "audio" | "pdf" | "zip" | "doc" | "other";

/** Surface-specific badge copy paired with a shared semantic, theme-aware presentation tone. */
export interface FileKind {
	tone: FileKindTone;
	label: string;
}

const RECIPIENT_LABELS: Record<FileKindTone, string> = {
	image: "IMG",
	video: "MP4",
	audio: "WAV",
	pdf: "PDF",
	zip: "ZIP",
	doc: "DOC",
	other: "FILE",
};

const EXTENSION_TO_TONE: Record<string, FileKindTone> = {
	png: "image", jpg: "image", jpeg: "image", gif: "image", webp: "image",
	svg: "image", heic: "image", avif: "image", bmp: "image", tiff: "image",
	mp4: "video", mov: "video", avi: "video", mkv: "video", webm: "video", m4v: "video",
	wav: "audio", mp3: "audio", flac: "audio", aac: "audio", ogg: "audio", m4a: "audio",
	pdf: "pdf",
	zip: "zip", rar: "zip", "7z": "zip", tar: "zip", gz: "zip",
	doc: "doc", docx: "doc", txt: "doc", rtf: "doc", odt: "doc", pages: "doc", md: "doc",
};

function extensionOf(fileName: string): string {
	const dot = fileName.lastIndexOf(".");
	return dot > 0 ? fileName.slice(dot + 1).toLowerCase() : "";
}

/** One semantic classifier owns theme tone selection for every file-badge surface. */
export function fileKindTone(fileName: string): FileKindTone {
	return EXTENSION_TO_TONE[extensionOf(fileName)] ?? "other";
}

/** Recipient rows use the compact labels from the recipient-list design. */
export function recipientFileKind(fileName: string): FileKind {
	const tone = fileKindTone(fileName);
	return {tone, label: RECIPIENT_LABELS[tone]};
}

/** Sender surfaces preserve the selected file's format while sharing the recipient tone palette. */
export function senderFileKind(fileName: string): FileKind {
	const tone = fileKindTone(fileName);
	const extension = extensionOf(fileName);
	const label = tone === "image" ? "IMG"
		: tone === "zip" ? "ZIP"
			: extension.length > 0 ? extension.toUpperCase().slice(0, 4) : "FILE";
	return {tone, label};
}

/** Existing semantic tint/ink pairs used by file badges; every pair adapts with the active theme. */
export function fileKindPalette(tokens: ModeTokens, tone: FileKindTone): {surface: string; ink: string} {
	switch (tone) {
		case "image":
			return {surface: tokens.signalTint, ink: tokens.signalInk};
		case "video":
			// `violetInk`, not `violet` (ui#2357): the composer's accent wash lightens the panel this
			// badge composites onto in dark mode, and plain violet measured under the 4.5:1 floor there.
			return {surface: tokens.accentTint, ink: tokens.violetInk};
		case "audio":
			return {surface: tokens.goldTint, ink: tokens.ink};
		case "pdf":
			return {surface: tokens.field, ink: tokens.errorInk};
		case "zip":
		case "doc":
			return {surface: tokens.accentTint, ink: tokens.segInk};
		case "other":
			return {surface: tokens.chip, ink: tokens.ink2};
	}
}
